import React from "react";
import PropTypes from "prop-types";
import { List as AntList, Icon, Tooltip } from "antd";
import { formatCurrency } from "utilities/currency";

const ListItem = AntList.Item;
const ListItemMeta = AntList.Item.Meta;

// D/E post and TOL/TNW post combine the case's proposed_limit (always
// absolute rupees) with this company's financial figures, whose unit
// convention (rupees vs thousands vs lacs) is inconsistent across
// cases/companies (confirmed on live data). A value past this magnitude is
// flagged as unreliable rather than presented as a normal ratio — never
// auto-scaled client-side, since we can't tell WHICH unit the figures are in.
const UNRELIABLE_RATIO_THRESHOLD = 1000;

// Values arrive already Number()-parsed by the financials reducer (raw API
// values are DRF DecimalField strings), but this stays defensive in case a
// caller ever passes a plain fetch response straight through.
const num = v => {
    if (v === null || v === undefined || v === "") {
        return null;
    }
    const n = typeof v === "number" ? v : parseFloat(v);
    return Number.isFinite(n) ? n : null;
};

const fmtRatio = v => {
    const n = num(v);
    return n === null ? "--" : n.toFixed(2);
};
const fmtDays = v => {
    const n = num(v);
    return n === null ? "--" : String(Math.round(n));
};
const fmtCurrency = v => {
    const n = num(v);
    return n === null ? "--" : formatCurrency(n, true);
};

// `.get(key)` (not dot-access) on every row — several of these field names
// also have a same-named getter on FinancialDetailRecord that recomputes a
// client-side figure from the raw pnl_*/bs_* inputs (kept for the OTHER
// tables/forms on this tab that already depend on that behaviour). This table
// intentionally reads the backend's own computed value instead, via
// Immutable Record's `.get`, which reads the stored value directly and is
// NOT shadowed by the subclass getter.
const RATIO_ROWS = [
    { label: "Inventory T/O Days", key: "ratio_inventory_turnover_days", fmt: fmtDays },
    { label: "Debtor T/O Days", key: "ratio_debtor_turnover_days_sheet", fmt: fmtDays },
    { label: "Creditor T/O Days", key: "ratio_creditor_turnover_days_sheet", fmt: fmtDays },
    { label: "Working Capital Cycle", key: "ratio_working_capital_cycle", fmt: fmtDays },
    { label: "Interest Coverage Ratio", key: "ratio_interest_coverage_ratio", fmt: fmtRatio },
    { label: "Debt Equity", key: "ratio_debt_equity", fmt: fmtRatio },
    { label: "TOL/TNW", key: "ratio_tol_by_tnw", fmt: fmtRatio },
    { label: "D/E post", key: "ratio_debt_equity_post", fmt: fmtRatio, unreliable: true },
    { label: "TOL/TNW Post", key: "ratio_tol_by_tnw_post", fmt: fmtRatio, unreliable: true },
    { label: "Current Ratio", key: "ratio_current_ratio", fmt: fmtRatio },
    { label: "PBITD+ Dir Remunerations", key: "ratio_pbitd_dir_remuneration", fmt: fmtCurrency },
    { label: "Debt Service", key: "ratio_debt_service", fmt: fmtCurrency },
    // Not ratio_dscr — that's a legacy field needing a different, rarely
    // populated input, usually 0.
    { label: "DSCR", key: "ratio_dscr_estimated", fmt: fmtRatio }
];

const ANALYSIS_ROWS = [
    {
        label: "Year",
        value: r => r && r.getIn(["financial_year", "name"]),
        raw: true
    },
    // Despite the row label this is gross Sales only — pnl_net_sales (below)
    // is where "net sales" actually lives.
    { label: "Net Sales", key: "pnl_revenue_sales", fmt: fmtCurrency },
    { label: "Other Operating Income", key: "pnl_revenue_other_income", fmt: fmtCurrency },
    { label: "Total Sales", key: "pnl_net_sales", fmt: fmtCurrency },
    { label: "EBIDTA", key: "ratio_ebitda", fmt: fmtCurrency },
    {
        label: "PAT",
        key: "pnl_pat",
        fmt: fmtCurrency,
        note:
            "Mapped to pnl_pat. The source spreadsheet's \"PAT\" cell actually " +
            "references a PBT cell (a bug in the sheet) — this table uses the " +
            "semantically-correct field instead of reproducing that bug."
    },
    { label: "Net Worth", key: "bs_liabilities_networth_equity", fmt: fmtCurrency },
    { label: "Quasi Equity (loans from friends & family)", key: "bs_liabilities_networth_quasi_equity", fmt: fmtCurrency },
    { label: "Total Net Worth incl quasi equity (TNW)", key: "bs_liabilities_tnw", fmt: fmtCurrency },
    {
        label: "Long Term Debt (LTD)",
        // No single API field matches this — computed client-side as the sum
        // of the two long-term borrowing components.
        value: r =>
            r &&
            (num(r.get("bs_liabilities_gte1yr_term_loan")) || 0) +
                (num(r.get("bs_liabilities_gte1yr_unsecured_loan")) || 0),
        fmt: fmtCurrency
    },
    { label: "Short Term Debt (STD)", key: "bs_liabilities_lt1yr_cc_od", fmt: fmtCurrency },
    { label: "Total Debt (Incl Unsecured Loans)", key: "bs_total_debit", fmt: fmtCurrency },
    {
        label: "Total O/s Liability (TOL)",
        key: "bs_tol",
        fmt: fmtCurrency,
        note:
            "Mapped to bs_tol (includes unsecured loans). The source " +
            "spreadsheet's \"TOL (excluding unsecured loans)\" label doesn't " +
            "match its own formula, which does include them — this table " +
            "follows the formula, not the (buggy) label."
    },
    { label: "Debt Equity Ratio", key: "ratio_debt_equity", fmt: fmtRatio },
    { label: "TOL / TNW", key: "ratio_tol_by_tnw", fmt: fmtRatio },
    { label: "Current Ratio", key: "ratio_current_ratio", fmt: fmtRatio },
    { label: "Interest Coverage Ratio", key: "ratio_interest_coverage_ratio", fmt: fmtRatio }
];

// Cell value for one row × one financial-year record.
const cellFor = (row, record) => {
    if (!record) {
        return null;
    }
    const raw = row.value ? row.value(record) : record.get(row.key);
    return row.raw ? raw : (row.fmt ? row.fmt(raw) : raw);
};

const RowLabel = ({ row }) => (
    <span>
        {row.label}
        {row.note ? (
            <Tooltip title={row.note}>
                <Icon type={"info-circle"} style={{ marginLeft: 6, color: "#8a93a3" }} />
            </Tooltip>
        ) : null}
    </span>
);

const RatioValue = ({ row, record }) => {
    const value = cellFor(row, record);
    if (row.unreliable) {
        const n = record ? num(row.value ? row.value(record) : record.get(row.key)) : null;
        const looksUnreliable =
            n !== null && Math.abs(n) > UNRELIABLE_RATIO_THRESHOLD;
        if (looksUnreliable) {
            return (
                <Tooltip
                    title={
                        "This value looks implausibly large. It combines the case's " +
                        "proposed limit (absolute ₹) with this company's financial " +
                        "figures — whose unit convention (₹ / thousands / lacs) is " +
                        "not consistent across cases. Treat as unreliable; not " +
                        "auto-scaled."
                    }
                >
                    <span style={{ color: "#d4380d", fontWeight: 600 }}>
                        <Icon type={"warning"} style={{ marginRight: 4 }} />
                        {value}
                    </span>
                </Tooltip>
            );
        }
    }
    return <span>{value}</span>;
};

/**
 * Financial Ratios & Analysis — two stacked read-only tables (Ratios, and
 * Financial Analysis (In lacs)) driven entirely by backend-computed fields on
 * the case's financial-detail records (GET /company/:id/financial-detail/),
 * NOT recomputed client-side — see RATIO_ROWS / ANALYSIS_ROWS above for the
 * exact field mapping and the two caveats (D/E post reliability, and the two
 * fields intentionally not matching the source spreadsheet's buggy formulas).
 *
 * `records` — financial-detail records for the columns to show, OLDEST FIRST
 * (left → right), already limited to however many years the caller wants
 * shown (today: the two most recent).
 */
const FinancialRatiosAnalysisTable = ({ records, title, name }) => {
    const isAnalysis = name === "analysis";
    const rows = isAnalysis ? ANALYSIS_ROWS : RATIO_ROWS;
    const cols = records || [];
    return (
        <AntList
            size={"small"}
            className={"tabulated-list"}
            itemLayout={"horizontal"}
            style={{ padding: 0 }}
            dataSource={rows}
            header={
                <ListItem key={"header"}>
                    <ListItemMeta title={title} />
                    <div className={"d-flex"}>
                        {cols.map((record, i) => (
                            <div
                                key={i}
                                style={{ width: "100px", textAlign: "right" }}
                            >
                                {/* Table 2 already carries the year as its own
                                    first row ("Year") — showing it again here
                                    would just duplicate it. */}
                                {!isAnalysis &&
                                    record &&
                                    record.getIn(["financial_year", "name"])}
                            </div>
                        ))}
                    </div>
                </ListItem>
            }
            renderItem={row => (
                <ListItem key={row.label}>
                    <ListItemMeta title={<RowLabel row={row} />} />
                    <div className={"d-flex"}>
                        {cols.map((record, i) => (
                            <div
                                key={i}
                                style={{ width: "100px", textAlign: "right" }}
                            >
                                {row.raw ? (
                                    cellFor(row, record) || "--"
                                ) : (
                                    <RatioValue row={row} record={record} />
                                )}
                            </div>
                        ))}
                    </div>
                </ListItem>
            )}
        />
    );
};

FinancialRatiosAnalysisTable.propTypes = {
    // Financial-detail records for the columns to render, oldest first.
    records: PropTypes.array,
    title: PropTypes.string,
    // "ratios" (default) or "analysis".
    name: PropTypes.string
};

export default FinancialRatiosAnalysisTable;
